/**
 * Series Data Store
 *
 * Holds large arrays (variations, composed prompts) OUTSIDE React Flow node state
 * to prevent re-render cascades when mutating 1000+ items.
 *
 * Node state only stores lightweight metadata (counts, status).
 * Components read large data directly from this store.
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { GeneratedVariation } from '@/lib/canvas/utils/variationGenerator'
import type { ComposedPrompt } from '@/lib/canvas/utils/promptComposer'

interface SeriesDataState {
  // Large data keyed by nodeId
  variations: Map<string, GeneratedVariation[]>
  composedPrompts: Map<string, ComposedPrompt[]>

  // Actions
  setVariations: (nodeId: string, variations: GeneratedVariation[]) => void
  updateVariations: (nodeId: string, updater: (prev: GeneratedVariation[]) => GeneratedVariation[]) => void
  setComposedPrompts: (nodeId: string, prompts: ComposedPrompt[]) => void
  updateComposedPrompts: (nodeId: string, updater: (prev: ComposedPrompt[]) => ComposedPrompt[]) => void
  clearNode: (nodeId: string) => void
  clearAll: () => void

  // Hydration (from persisted node state on canvas load)
  hydrateFromNodes: (nodes: Array<{ id: string; type?: string; data: Record<string, unknown> }>) => void

  // Re-read only these nodes, leaving every other node's data alone
  adoptFromNodes: (nodes: Array<{ id: string; type?: string; data: Record<string, unknown> }>) => void

  // Serialization (inject data back into nodes for save)
  injectForSave: (nodes: Array<Record<string, unknown>>) => Array<Record<string, unknown>>
}

export const useSeriesDataStore = create<SeriesDataState>()(
  subscribeWithSelector((set, get) => ({
    variations: new Map(),
    composedPrompts: new Map(),

    setVariations: (nodeId, variations) => {
      set((state) => {
        const next = new Map(state.variations)
        next.set(nodeId, variations)
        return { variations: next }
      })
    },

    updateVariations: (nodeId, updater) => {
      set((state) => {
        const prev = state.variations.get(nodeId) ?? []
        const next = new Map(state.variations)
        next.set(nodeId, updater(prev))
        return { variations: next }
      })
    },

    setComposedPrompts: (nodeId, prompts) => {
      set((state) => {
        const next = new Map(state.composedPrompts)
        next.set(nodeId, prompts)
        return { composedPrompts: next }
      })
    },

    updateComposedPrompts: (nodeId, updater) => {
      set((state) => {
        const prev = state.composedPrompts.get(nodeId) ?? []
        const next = new Map(state.composedPrompts)
        next.set(nodeId, updater(prev))
        return { composedPrompts: next }
      })
    },

    clearNode: (nodeId) => {
      set((state) => {
        const nextV = new Map(state.variations)
        const nextP = new Map(state.composedPrompts)
        nextV.delete(nodeId)
        nextP.delete(nodeId)
        return { variations: nextV, composedPrompts: nextP }
      })
    },

    clearAll: () => {
      set({ variations: new Map(), composedPrompts: new Map() })
    },

    hydrateFromNodes: (nodes) => {
      const variations = new Map<string, GeneratedVariation[]>()
      const composedPrompts = new Map<string, ComposedPrompt[]>()

      for (const node of nodes) {
        const state = (node.data?.state ?? node.data) as Record<string, unknown> | undefined
        if (!state) continue

        if (node.type === 'VariationGenerator' && Array.isArray(state.variations) && state.variations.length > 0) {
          variations.set(node.id, state.variations as GeneratedVariation[])
        }
        if (node.type === 'PromptComposer' && Array.isArray(state.composedPrompts) && state.composedPrompts.length > 0) {
          composedPrompts.set(node.id, state.composedPrompts as ComposedPrompt[])
        }
      }

      set({ variations, composedPrompts })
    },

    /**
     * Used when the canvas is reconciled with the server: the nodes whose
     * server version won the merge bring their series data with them, and it
     * has to replace what this tab holds for those nodes — but only for those,
     * or edits made here that won the merge would be thrown away.
     */
    adoptFromNodes: (nodes) => {
      if (nodes.length === 0) return
      set((state) => {
        const variations = new Map(state.variations)
        const composedPrompts = new Map(state.composedPrompts)

        for (const node of nodes) {
          const nodeState = (node.data?.state ?? node.data) as Record<string, unknown> | undefined
          const nextVariations = nodeState?.variations
          const nextPrompts = nodeState?.composedPrompts

          if (Array.isArray(nextVariations)) variations.set(node.id, nextVariations as GeneratedVariation[])
          else variations.delete(node.id)

          if (Array.isArray(nextPrompts)) composedPrompts.set(node.id, nextPrompts as ComposedPrompt[])
          else composedPrompts.delete(node.id)
        }

        return { variations, composedPrompts }
      })
    },

    injectForSave: (nodes) => {
      const state = get()
      return nodes.map((node) => {
        const nodeId = node.id as string
        const nodeData = node.data as Record<string, unknown> | undefined
        const nodeState = nodeData?.state as Record<string, unknown> | undefined
        if (!nodeState) return node

        const variations = state.variations.get(nodeId)
        const prompts = state.composedPrompts.get(nodeId)

        if (!variations && !prompts) return node

        return {
          ...node,
          data: {
            ...nodeData,
            state: {
              ...nodeState,
              ...(variations ? { variations } : {}),
              ...(prompts ? { composedPrompts: prompts } : {}),
            },
          },
        }
      })
    },
  }))
)
